'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Download, X, Share } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandMark } from '@/components/brand/BrandLogo';
import { trackGrowthEvent } from '@/lib/analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<unknown>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

const PWA_SUPPRESSED_PATHS = ['/login', '/signup', '/forgot-password'];
const IOS_PROMPT_DISMISSAL_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_VISITS_BEFORE_PROMPT = 3;

const shouldSuppressInstallPrompt = (pathname: string) =>
  PWA_SUPPRESSED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export default function PWAInstallPrompt() {
  const pathname = usePathname();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isEligible] = useState(() => {
    if (typeof window === 'undefined') return false;

    let visits = Number.parseInt(localStorage.getItem('youraura-visits') || '0', 10);
    if (!sessionStorage.getItem('youraura-visit-counted')) {
      visits += 1;
      localStorage.setItem('youraura-visits', String(visits));
      sessionStorage.setItem('youraura-visit-counted', 'true');
    }
    return visits >= MIN_VISITS_BEFORE_PROMPT;
  });
  const [isIOS] = useState(
    () =>
      typeof window !== 'undefined' &&
      /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase())
  );
  const [isStandalone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches
  );
  const [hideIOSPrompt, setHideIOSPrompt] = useState(() => {
    if (typeof window === 'undefined') return false;

    const iosDismissed = window.localStorage.getItem('pwa-ios-prompt-dismissed');
    return Boolean(
      iosDismissed && Date.now() - Number.parseInt(iosDismissed, 10) < IOS_PROMPT_DISMISSAL_MS
    );
  });
  const isSuppressedRoute = shouldSuppressInstallPrompt(pathname ?? '');

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch((error) => {
        console.error('[PWA] Service worker registration failed:', error);
      });
    }

  }, []);

  useEffect(() => {
    if (isSuppressedRoute || !isEligible) {
      return;
    }

    // Don't show PWA prompt on localhost (PWA install doesn't work reliably on localhost)
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.port === '4028';

    if (isLocalhost) {
      return;
    }

    // Handle standard PWA event
    const handler = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
      setIsVisible(true);
      trackGrowthEvent('pwa_install_prompted', { platform: 'browser' });
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isEligible, isSuppressedRoute]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.error('[PWA] No deferred prompt available');
      alert(
        "Installation prompt is not available. This may be because:\n\n• The app is already installed\n• You're on an unsupported browser\n• The site is accessed via HTTP (requires HTTPS)\n• Pop-ups are blocked\n\nTry:\n1. Use Chrome, Edge, or Samsung Internet\n2. Ensure you're on HTTPS\n3. Allow pop-ups for this site"
      );
      return;
    }

    try {
      await deferredPrompt.prompt();

      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsVisible(false);
        trackGrowthEvent('pwa_installed', { platform: 'browser' });
        // Show success message
        setTimeout(() => {
          alert(
            'YourAura installed successfully. You can now access your daily reading space from your home screen.'
          );
        }, 500);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('[PWA] Error during installation:', error);
      alert('Installation failed. Please try again or install manually from your browser menu.');
    }
  };

  // Show iOS instructions if on iOS and not installed
  if (isSuppressedRoute) return null;

  if (isIOS && isEligible && !isStandalone && !hideIOSPrompt) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:bottom-8 md:w-96 z-50 pointer-events-auto"
        >
          <div className="experience-surface relative rounded-2xl p-4">
            <button
              onClick={() => {
                localStorage.setItem('pwa-ios-prompt-dismissed', Date.now().toString());
                setHideIOSPrompt(true);
                setIsVisible(false);
              }}
              className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-lg text-muted-foreground hover:bg-[var(--experience-surface-raised)] hover:text-foreground transition-colors"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                <BrandMark size="lg" className="h-11 w-11" />
              </div>
              <div className="flex-1">
                <h4 className="text-foreground font-bold text-sm">Keep YourAura close</h4>
                <p className="text-xs text-muted-foreground">
                  Daily readings, reminders, and saved history
                </p>
              </div>
            </div>

            <p className="text-sm text-[var(--experience-text-soft)]">
              Add the app so tomorrow&apos;s reading can start from your saved context. Tap{' '}
              <Share className="inline w-4 h-4 mx-1" /> then{' '}
              <strong>&quot;Add to Home Screen&quot;</strong>
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:bottom-8 md:w-96 z-50 pointer-events-auto"
        >
          <div className="experience-surface relative overflow-hidden rounded-2xl p-4">

            <button
              onClick={() => setIsVisible(false)}
              className="absolute right-2 top-2 z-20 grid h-11 w-11 place-items-center rounded-lg text-muted-foreground hover:bg-[var(--experience-surface-raised)] hover:text-foreground transition-colors"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-4 relative z-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-accent/25 bg-accent/10">
                <BrandMark size="lg" className="h-11 w-11" />
              </div>
              <div className="flex-1">
                <h4 className="text-foreground font-bold text-sm">Install YourAura</h4>
                <p className="text-xs text-muted-foreground">
                  Open daily readings, memory, and reminders faster.
                </p>
              </div>
            </div>

            <button
              onClick={handleInstall}
              className="manuscript-button mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-bold"
            >
              <Download size={16} />
              Add to Home Screen
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
